<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{ 'block-date-popup': true, 'is-visible': isVisible }">
    <h2>{{ $t('blockDatePopup.title') }}</h2>

    <div class="block-date-popup-wrapper">
      <field
        type="full-width">
        <label slot="label">
          {{ $t('blockDatePopup.howLong') }}
        </label>

        <input
          slot="field"
          type="number"
          min="1"
          :class="{ 'is-invalid': this.errors.indexOf('duration') > -1 }"
          @keyup="errors = []"
          v-model="duration" />
      </field>

      <field
        type="full-width">
        <label slot="label">
          {{ $t('blockDatePopup.name') }}
        </label>

        <input
          slot="field"
          type="text"
          min="1"
          v-model="name" />
      </field>

      <field
        type="full-width">
        <label slot="label">
          {{ $t('blockDatePopup.phone') }}
        </label>

        <vue-tel-input
          slot="field"
          :defaultCountry="$store.state.config.company.phone_prefix"
          autocomplete="off"
          :disabledFetchingCountry="true"
          :dynamicPlaceholder="false"
          :placeholder="''"
          ref="customer-phone-field"
          inputId="block-date-customer-phone-field"
          :required="false"
          :validCharactersOnly="true"
          v-model="phone"
          @focus="setCursorAtEndOfPhone"
          @input="updatePhone" />
      </field>

      <field
        type="full-width">
        <label slot="label">
          {{ $t('blockDatePopup.email') }}
        </label>

        <input
          slot="field"
          type="text"
          min="1"
          v-model="confirm_email" />
      </field>
    </div>

    <btn
      @click.native="blockDate"
      type="popup-left primary"
      :disabled="blockInProgress">
      <template v-if="!blockInProgress">{{ $t('blockDatePopup.blockDate') }}</template>
      <template v-if="blockInProgress">{{ $t('blockDatePopup.blockingDate') }}</template>
    </btn>

    <btn
      @click.native="cancel"
      type="popup-right">
      {{ $t('common.ui.cancel') }}
    </btn>
  </div>
</template>

<script>
import Configuration from './../mixins/Configuration';
import ErrorUtils from './../helpers/ErrorUtils.js';
import PopupZindex from './../mixins/PopupZindex';
import UserTypes from './../mixins/UserTypes';
import { VueTelInput } from 'vue-tel-input';

export default {
  name: 'block-date-popup',
  mixins: [
    Configuration,
    UserTypes,
    PopupZindex
  ],
  components: {
    'vue-tel-input': VueTelInput
  },
  data () {
    return {
      errors: [],
      isVisible: false,
      blockInProgress: false,
      duration: '30',
      name: '',
      phone: '',
      confirm_email: '',
      reservationConfig: null
    };
  },
  mounted () {
    this.$bus.$on('show-block-date-popup', this.show);
  },
  methods: {
    show (data) {
      this.reservationConfig = JSON.parse(JSON.stringify(data.reservationConfig));
      this.duration = '30';
      this.name = '';
      this.phone = '';
      this.confirm_email = '';
      this.blockInProgress = false;
      this.isVisible = true;
      this.increasePopupZindex();
    },
    hide () {
      this.reservationConfig = null;
      this.duration = '30';
      this.name = '';
      this.phone = '';
      this.confirm_email = '';
      this.blockInProgress = false;
      this.isVisible = false;
      this.errors = [];
      this.removePopupZindex();
      this.$bus.$emit('search-date-popup-blockade-added');
    },
    setCursorAtEndOfPhone () {
      setTimeout(() => {
        let valueToSet = this.$refs['customer-phone-field'].$refs['input'].value;
        this.$refs['customer-phone-field'].$refs['input'].value = '';
        this.$refs['customer-phone-field'].$refs['input'].value = valueToSet;
      }, 0);
    },
    updatePhone (newPhone, newPhoneData) {
      if (newPhoneData.number.international) {
        this.phone = newPhoneData.number.international;
      } else {
        this.phone = '';
      }
    },
    blockDate () {
      if (isNaN(this.duration) || parseInt(this.duration, 10) <= 0) {
        this.errors = ['duration'];
        return;
      }

      this.blockInProgress = true;
      let config = JSON.parse(JSON.stringify(this.reservationConfig));
      config.blocking_time = parseInt(this.duration, 10);

      if (this.confirm_email) {
        config.confirm_email = this.confirm_email;
      }

      if (this.name) {
        config.name = this.name;
      }

      if (this.phone) {
        config.phone = this.phone;
      }

      this.$http.post('/panel/blocked-termins/block', config).then(response => {
        this.blockInProgress = false;

        if (response.data.success) {
          this.$bus.$emit('notification-display', {
            type: 'success',
            message: this.$t('blockDatePopup.dateBlocked')
          });

          this.$bus.$emit('calendar-day-popup-reload-reservations');
          this.$bus.$emit('calendar-layout-reload-needed');
          this.$bus.$emit('blocked-termins-list-reload-needed');
          this.hide();
        } else {
          this.$bus.$emit('alert-display', {
            title: this.$t('blockDatePopup.errorTitle'),
            desc: this.$t('blockDatePopup.errorDesc'),
            okLabel: this.$t('common.ui.ok'),
            okClick: () => true,
            isDanger: true
          });
        }
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/blocked-termins/block', this.$router, this.$bus, this.loadAvailableDates.bind(this));
      });
    },
    cancel () {
      this.hide();
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-block-date-popup', this.show);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.block-date-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, .125);
  display: none;
  left: 50%;
  padding: 3rem 4rem 8rem 4rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 480px;

  &.is-visible {
    display: block;
  }

  .field {
    label {
      margin-bottom: 1rem;
      text-align: left;
      width: 100%!important;
    }

    input,
    .vue-tel-input {
      width: 100%!important;
    }
  }

  h2 {
    font-weight: 400;
    line-height: 1;
    margin: 0 0 2rem 0;
  }

  .button-popup-left {
    border-radius: 0 0 0 $border-radius-small;
  }

  .button-popup-right {
    border-radius: 0 0 $border-radius-small 0;
  }
}

@media (max-width: 640px) {
  .block-date-popup {
    border-radius: 0;
    height: 100%;
    left: 0;
    padding: 2rem 2rem 6rem 2rem;
    top: 0;
    transform: none;
    width: 100vw;

    .button-popup-left {
      border-radius: 0;
    }

    .button-popup-right {
      border-radius: 0;
    }

    &-wrapper {
      max-height: 100%;
      overflow: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
}

@media (max-width: 560px) {
  .block-date-popup {
    .field {
      padding: 0 0 1rem 0;

      label {
        padding: 0 0 .5rem 0;
        text-align: left;
        width: 100%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/BlockDatePopup.vue